import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Tooltip from '@mui/material/Tooltip';

import { ChevronDown, X } from 'lucide-react';

import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE, NORMS } from '../../constants/layers.ts';
import type { PhotometricTarget } from '../../utils/photometricTargets';
import { CustomTargetsForm } from '../photometric-targets/CustomTargetsForm';
import { LayerSetting } from '../project-settings/types.ts';

export type NewProjectLayerPopupData = {
  layerName: string; // threeJsName
  screenX: number;
  screenY: number;
};

type Props = {
  data: NewProjectLayerPopupData;
  layerSettings: LayerSetting[];
  onToggleChange: (name: string, field: 'illuminated' | 'canHoldLight', value: boolean) => void;
  onNormChange: (name: string, value: string) => void;
  onCustomTargets: (name: string, targets: PhotometricTarget[]) => void;
  onClose: () => void;
  /** Called on pointerdown inside the popup — lets the parent skip canvas clicks. */
  onPointerDown?: () => void;
};

export function NewProjectLayerPopup({
  data,
  layerSettings,
  onToggleChange,
  onNormChange,
  onCustomTargets,
  onClose,
  onPointerDown,
}: Props) {
  const { t } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);
  const normBtnRef = useRef<HTMLButtonElement>(null);
  const [normDropdownOpen, setNormDropdownOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [normFilter, setNormFilter] = useState('');
  const [customOpen, setCustomOpen] = useState(false);

  const layer = layerSettings.find((l) => l.threeJsName === data.layerName);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleLMNToggle = useCallback(() => {
    if (!layer) return;
    const newValue = !layer.illuminated;
    // Only call onToggleChange — the parent (handleIlluminatedClick) already
    // resets the norm to LAYER_NORM_NONE when turning illuminated OFF.
    // Calling onNormChange separately would race and overwrite with stale state.
    onToggleChange(layer.threeJsName, 'illuminated', newValue);
  }, [layer, onToggleChange]);

  const handleNormSelect = useCallback(
    (norm: string) => {
      if (!layer) return;
      onNormChange(layer.threeJsName, norm);
      setCustomOpen(false);
      setNormDropdownOpen(false);
      setNormFilter('');
    },
    [layer, onNormChange],
  );

  if (!layer) return null;

  // The quick-edit bubble stays the size of the click. Custom targets open in a card beside it.
  const isCustom = layer.norm === LAYER_NORM_CUSTOM || !!layer.customTargets?.length;
  const showCustom = layer.illuminated && (isCustom || customOpen);
  const popupWidth = 180;
  const popupHeight = 140;
  const currentNorm = layer.illuminated && layer.norm && layer.norm !== LAYER_NORM_NONE ? layer.norm : null;
  const normLabel = isCustom ? t('workspace.customTargets.chip') : currentNorm;
  const x = Math.min(Math.max(8, data.screenX), window.innerWidth - popupWidth - 16);
  const y = Math.min(Math.max(8, data.screenY), window.innerHeight - popupHeight - 16);
  const stackCard = window.innerWidth < popupWidth + 308;
  const openLeft = !stackCard && x + popupWidth + 308 > window.innerWidth;
  const alignBottom = y + 380 > window.innerHeight;

  const normsToShow = NORMS.filter((n) => n !== LAYER_NORM_NONE);

  return (
    <div
      ref={popupRef}
      className="fixed z-50"
      style={{ left: x, top: y, width: popupWidth, pointerEvents: 'auto' }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => {
        e.stopPropagation();
        onPointerDown?.();
      }}
    >
      <div
        className="shadow-xl"
        style={{
          borderRadius: 16,
          background: '#313131',
          border: '1px solid #444',
          padding: '12px 14px',
        }}
      >
        {/* Header: layer name + close */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-gray-300 truncate max-w-[130px]" title={layer.displayName}>
            {layer.displayName}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-0.5 -mr-1 shrink-0">
            <X size={14} />
          </button>
        </div>

        {/* LMN toggle row */}
        <div className="flex items-center justify-between mb-2">
          <Tooltip title={t('workspace.lmnTooltip')} arrow>
            <span className="text-[11px] text-gray-300 uppercase tracking-wider cursor-help">LMN</span>
          </Tooltip>
          <button
            onClick={handleLMNToggle}
            className="relative w-9 h-5 rounded-full transition-colors duration-200 cursor-pointer"
            style={{ backgroundColor: '#000' }}
          >
            <div
              className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ${
                layer.illuminated ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Norm dropdown row */}
        {layer.illuminated && (
          <div className="relative">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-300 uppercase tracking-wider">{t('workspace.norm', 'Norm')}</span>
              <button
                ref={normBtnRef}
                onClick={() => {
                  if (!normDropdownOpen && normBtnRef.current) {
                    const rect = normBtnRef.current.getBoundingClientRect();
                    const DROPDOWN_HEIGHT = 150;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    setDropUp(spaceBelow < DROPDOWN_HEIGHT + 8);
                  }
                  setNormDropdownOpen(!normDropdownOpen);
                }}
                className="flex items-center gap-1 min-w-[56px] h-6 text-xs rounded px-2 cursor-pointer hover:bg-stone-600"
                style={{ backgroundColor: '#232323' }}
              >
                <span className={normLabel ? 'text-lime-400 font-medium' : 'text-gray-400'}>{normLabel ?? 'None'}</span>
                <ChevronDown
                  size={10}
                  className={`text-gray-400 transition-transform ${normDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
            </div>

            {/* Dropdown / drop-up list */}
            {normDropdownOpen && (
              <div
                className={`absolute right-0 w-[100px] rounded-lg z-10 ${dropUp ? 'mb-1' : 'mt-1'}`}
                style={{
                  backgroundColor: '#232323',
                  border: '1px solid #444',
                  ...(dropUp ? { bottom: '100%' } : {}),
                }}
              >
                <input
                  type="text"
                  value={normFilter}
                  onChange={(e) => setNormFilter(e.target.value)}
                  placeholder="Filter…"
                  autoFocus
                  className="w-full text-xs text-white bg-black/30 px-2 py-1 focus:outline-none border-b border-gray-600 rounded-t-lg"
                />
                <div className="overflow-y-auto" style={{ maxHeight: 120 }}>
                  {(!normFilter || 'none'.includes(normFilter.toLowerCase())) && (
                    <button
                      onClick={() => handleNormSelect(LAYER_NORM_NONE)}
                      className={`w-full text-left text-xs px-2 py-1 hover:bg-stone-600 transition-colors ${
                        !currentNorm ? 'text-lime-400 font-medium' : 'text-gray-400'
                      }`}
                    >
                      None
                    </button>
                  )}
                  {(!normFilter ||
                    t('workspace.customTargets.chip').toLowerCase().includes(normFilter.toLowerCase())) && (
                    <button
                      onClick={() => {
                        setCustomOpen(true);
                        setNormDropdownOpen(false);
                        setNormFilter('');
                      }}
                      className={`w-full text-left text-xs px-2 py-1 hover:bg-stone-600 transition-colors ${
                        isCustom ? 'text-lime-400 font-medium' : 'text-gray-300'
                      }`}
                    >
                      {t('workspace.customTargets.chip')}
                    </button>
                  )}
                  {normsToShow
                    .filter((n) => !normFilter || n.toLowerCase().includes(normFilter.toLowerCase()))
                    .map((norm) => (
                      <button
                        key={norm}
                        onClick={() => handleNormSelect(norm)}
                        className={`w-full text-left text-xs px-2 py-1 hover:bg-stone-600 transition-colors ${
                          layer.norm === norm ? 'text-lime-400 font-medium' : 'text-gray-300'
                        }`}
                      >
                        {norm}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showCustom && (
        <div
          className={`absolute max-h-[min(420px,calc(100vh-24px))] overflow-y-auto shadow-xl ${
            stackCard
              ? 'top-[calc(100%+8px)]'
              : `${openLeft ? 'right-[calc(100%+8px)]' : 'left-[calc(100%+8px)]'} w-[300px] ${
                  alignBottom ? 'bottom-0' : 'top-0'
                }`
          }`}
          style={{
            borderRadius: 16,
            background: '#313131',
            border: '1px solid #444',
            padding: '12px 14px',
            ...(stackCard ? { left: 8 - x, width: Math.min(300, window.innerWidth - 16) } : {}),
          }}
        >
          <p className="mb-2 text-[11px] uppercase tracking-wider text-gray-300">{t('workspace.customTargets.chip')}</p>
          <CustomTargetsForm
            value={layer.customTargets ?? []}
            onApply={(targets) => onCustomTargets(layer.threeJsName, targets)}
          />
        </div>
      )}
    </div>
  );
}
